import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { supabase } from '../utils/supabaseClient';
import ServiceRateConfiguration from '../components/providers/ServiceRateConfiguration';
import MultipleEmailManager from '../components/providers/MultipleEmailManager';
import { fetchServiceTypes, saveProviderServiceRates } from '../utils/serviceTypeUtils';

function AddEditProvider() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  const [loading, setLoading] = useState(isEditMode);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  
  // Provider basic info
  const [providerName, setProviderName] = useState('');
  const [status, setStatus] = useState('active');
  const [netTerms, setNetTerms] = useState('');
  
  // Default rates for auto-enrollment
  const [defaultHourlyRate, setDefaultHourlyRate] = useState('');
  const [defaultFlatRate, setDefaultFlatRate] = useState('');
  const [defaultRefusalRate, setDefaultRefusalRate] = useState('');
  
  // Service types and rate configurations
  const [serviceTypes, setServiceTypes] = useState([]);
  const [serviceRateConfigs, setServiceRateConfigs] = useState({});
  const [loadingServiceTypes, setLoadingServiceTypes] = useState(true);
  
  // Track which service types have been initialized to prevent overwriting manual changes
  const [initializedServiceTypes, setInitializedServiceTypes] = useState(new Set());

  // Define fetchProvider before using it in useEffect
  const fetchProvider = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch provider basic info
      const { data: providerData, error: providerError } = await supabase
        .from('providers')
        .select('*')
        .eq('id', id)
        .single();
        
      if (providerError) throw providerError;
      
      if (providerData) {
        setProviderName(providerData.name || '');
        setStatus(providerData.status || 'active');
        setNetTerms(providerData.net_terms || '');
        setDefaultHourlyRate(providerData.default_hourly_rate || '');
        setDefaultFlatRate(providerData.default_flat_rate || '');
        setDefaultRefusalRate(providerData.default_refusal_rate || '');
      }
      
      // Fetch provider service rate configurations
      const { data: rateData, error: rateError } = await supabase
        .from('provider_service_rates')
        .select('*')
        .eq('provider_id', id);
      
      if (rateError) {
        console.error('Error fetching rates:', rateError);
      } else if (rateData) {
        // Convert array to object keyed by service_type_id
        const rateConfigs = {};
        rateData.forEach(rate => {
          rateConfigs[rate.service_type_id] = rate;
        });
        setServiceRateConfigs(rateConfigs);
      }
      
    } catch (error) {
      console.error('Error fetching provider details:', error);
      setSaveError(`Error fetching provider details: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Load service types on mount
  useEffect(() => {
    loadServiceTypes();
  }, []);

  // Load provider data if editing
  useEffect(() => {
    if (isEditMode) {
      fetchProvider();
    }
  }, [id, isEditMode, fetchProvider]);

  // Auto-populate service type configurations from default rates (Add mode only)
  useEffect(() => {
    // Only run in Add mode when we have service types loaded
    if (isEditMode || serviceTypes.length === 0) {
      return;
    }

    // Only auto-populate if at least one default rate is provided
    const hasDefaultRates = defaultHourlyRate || defaultFlatRate || defaultRefusalRate;
    if (!hasDefaultRates) {
      return;
    }

    // Auto-populate uninitialized service types
    setServiceRateConfigs(prev => {
      const newConfigs = { ...prev };
      let hasUpdates = false;

      serviceTypes.forEach(serviceType => {
        // Skip if already initialized
        if (initializedServiceTypes.has(serviceType.id)) {
          return;
        }

        // Skip if already has a configuration with actual values (not just an empty shell)
        const existingConfig = prev[serviceType.id];
        if (existingConfig) {
          const hasActualValues = existingConfig.hourly_rate || 
                                  existingConfig.flat_rate || 
                                  existingConfig.per_location ||
                                  existingConfig.refusal_rate;
          if (hasActualValues) {
            return;
          }
        }

        const isInitialServiceCall = serviceType.name === 'Initial Service Call';
        const config = {
          service_type_id: serviceType.id,
          service_type_name: serviceType.name,
          rate_type: serviceType.rate_type
        };

        // Populate based on service type's billing type
        if (serviceType.rate_type === 'HOURLY') {
          config.hourly_rate = defaultHourlyRate ? parseFloat(defaultHourlyRate) : null;
        } else if (serviceType.rate_type === 'FLAT') {
          // Special case for Initial Service Call - populate per_location instead of flat_rate
          if (isInitialServiceCall) {
            config.per_location = defaultFlatRate ? parseFloat(defaultFlatRate) : null;
          } else {
            config.flat_rate = defaultFlatRate ? parseFloat(defaultFlatRate) : null;
          }
          config.refusal_rate = defaultRefusalRate ? parseFloat(defaultRefusalRate) : null;
        }

        newConfigs[serviceType.id] = config;
        hasUpdates = true;
      });

      // Only return new object if there are actual updates
      return hasUpdates ? newConfigs : prev;
    });
  }, [defaultHourlyRate, defaultFlatRate, defaultRefusalRate, serviceTypes, isEditMode, initializedServiceTypes]);

  const loadServiceTypes = async () => {
    try {
      setLoadingServiceTypes(true);
      const { data, error } = await fetchServiceTypes();
      
      if (error) {
        console.error('Error loading service types:', error);
        setSaveError('Failed to load service types');
        return;
      }
      
      setServiceTypes(data || []);
    } catch (error) {
      console.error('Error loading service types:', error);
      setSaveError(`Error loading service types: ${error.message}`);
    } finally {
      setLoadingServiceTypes(false);
    }
  };

  const handleServiceRateChange = (config) => {
    // Check if this is actually a change from the existing config
    const existingConfig = serviceRateConfigs[config.service_type_id];
    const isActualChange = !existingConfig || 
                          existingConfig.hourly_rate !== config.hourly_rate ||
                          existingConfig.flat_rate !== config.flat_rate ||
                          existingConfig.per_location !== config.per_location ||
                          existingConfig.refusal_rate !== config.refusal_rate;
    
    setServiceRateConfigs(prev => ({
      ...prev,
      [config.service_type_id]: config
    }));
    
    // Only mark as initialized if:
    // 1. It has actual rate values
    // 2. AND it's an actual change (not just echoing back auto-populated values)
    const hasActualValues = config.hourly_rate || 
                            config.flat_rate || 
                            config.per_location ||
                            config.refusal_rate;
    
    if (hasActualValues && isActualChange) {
      setInitializedServiceTypes(prev => new Set(prev).add(config.service_type_id));
    }
  };

  const validateForm = () => {
    if (!providerName.trim()) {
      setSaveError('Provider name is required');
      return false;
    }
    
    // Check that all service types have configurations
    const missingConfigs = serviceTypes.filter(
      st => !serviceRateConfigs[st.id] || !serviceRateConfigs[st.id].rate_type
    );
    
    if (missingConfigs.length > 0) {
      setSaveError(`Please configure rates for: ${missingConfigs.map(st => st.name).join(', ')}`);
      return false;
    }
    
    // Validate each configuration has required fields
    for (const config of Object.values(serviceRateConfigs)) {
      const isInitialServiceCall = config.service_type_name === 'Initial Service Call';
      
      if (config.rate_type === 'FLAT') {
        // Initial Service Call uses tiered rates (per_board/per_location or unit-based rates)
        // Other service types use simple flat_rate
        if (isInitialServiceCall) {
          // Check if at least one tiered rate field is configured (including $0 values)
          const hasTieredRates = config.per_board !== null && config.per_board !== undefined && config.per_board !== '' ||
                                 config.per_location !== null && config.per_location !== undefined && config.per_location !== '' ||
                                 config.rate_1_unit !== null && config.rate_1_unit !== undefined && config.rate_1_unit !== '' || 
                                 config.rate_2_units !== null && config.rate_2_units !== undefined && config.rate_2_units !== '' || 
                                 config.rate_3_units !== null && config.rate_3_units !== undefined && config.rate_3_units !== '' || 
                                 config.rate_4_units !== null && config.rate_4_units !== undefined && config.rate_4_units !== '' || 
                                 config.rate_5_units !== null && config.rate_5_units !== undefined && config.rate_5_units !== '' || 
                                 config.rate_6_units !== null && config.rate_6_units !== undefined && config.rate_6_units !== '' || 
                                 config.rate_7_units !== null && config.rate_7_units !== undefined && config.rate_7_units !== '' || 
                                 config.rate_8_units !== null && config.rate_8_units !== undefined && config.rate_8_units !== '' || 
                                 config.rate_9_units !== null && config.rate_9_units !== undefined && config.rate_9_units !== '' || 
                                 config.rate_10_units !== null && config.rate_10_units !== undefined && config.rate_10_units !== '';
          
          // Only validate in add mode or if rates are being changed
          // In edit mode, allow empty rates if user is just updating basic info
          if (!isEditMode && !hasTieredRates) {
            setSaveError(`At least one tiered rate configuration is required for ${config.service_type_name}`);
            return false;
          }
        } else {
          // Standard flat rate service types - allow $0 but require a value
          // Only validate in add mode or if rates are being changed
          if (!isEditMode && (config.flat_rate === null || config.flat_rate === undefined || config.flat_rate === '')) {
            setSaveError(`Flat rate is required for ${config.service_type_name}`);
            return false;
          }
          if (!isEditMode && config.flat_rate < 0) {
            setSaveError(`Flat rate cannot be negative for ${config.service_type_name}`);
            return false;
          }
        }
      } else if (config.rate_type === 'HOURLY') {
        // Hourly rate - allow $0 but require a value
        // Only validate in add mode or if rates are being changed
        if (!isEditMode && (config.hourly_rate === null || config.hourly_rate === undefined || config.hourly_rate === '')) {
          setSaveError(`Hourly rate is required for ${config.service_type_name}`);
          return false;
        }
        if (!isEditMode && config.hourly_rate < 0) {
          setSaveError(`Hourly rate cannot be negative for ${config.service_type_name}`);
          return false;
        }
      }
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setSaveError('');
      
      if (!validateForm()) {
        setLoading(false);
        return;
      }
      
      let providerId = id;
      
      if (isEditMode) {
        // Update existing provider
        const { error: updateError } = await supabase
          .from('providers')
          .update({
            name: providerName,
            status: status,
            net_terms: netTerms || null,
            default_hourly_rate: parseFloat(defaultHourlyRate) || null,
            default_flat_rate: parseFloat(defaultFlatRate) || null,
            default_refusal_rate: parseFloat(defaultRefusalRate) || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', id);
          
        if (updateError) throw updateError;
      } else {
        // Create new provider
        const { data: newProvider, error: insertError } = await supabase
          .from('providers')
          .insert([{
            name: providerName,
            status: status,
            net_terms: netTerms || null,
            default_hourly_rate: parseFloat(defaultHourlyRate) || null,
            default_flat_rate: parseFloat(defaultFlatRate) || null,
            default_refusal_rate: parseFloat(defaultRefusalRate) || null
          }])
          .select()
          .single();
          
        if (insertError) throw insertError;
        providerId = newProvider.id;
      }
      
      // Save service rate configurations - only save configs with actual rate values
      const serviceRatesArray = Object.values(serviceRateConfigs).filter(config => {
        // For HOURLY, must have hourly_rate
        if (config.rate_type === 'HOURLY') {
          return config.hourly_rate && config.hourly_rate > 0;
        }
        
        // For FLAT, check if it has any rate value
        if (config.rate_type === 'FLAT') {
          const hasStandardFlat = config.flat_rate && config.flat_rate > 0;
          const hasTieredRates = config.per_board || config.per_location || 
                                 config.rate_1_unit || config.rate_2_units || 
                                 config.rate_3_units || config.rate_4_units || 
                                 config.rate_5_units || config.rate_6_units || 
                                 config.rate_7_units || config.rate_8_units || 
                                 config.rate_9_units || config.rate_10_units;
          return hasStandardFlat || hasTieredRates;
        }
        
        return false;
      });
      
      // Only save if there are valid configurations
      if (serviceRatesArray.length > 0) {
        const { success: rateSuccess, error: rateError } = await saveProviderServiceRates(
          providerId,
          serviceRatesArray
        );
        
        if (!rateSuccess || rateError) {
          throw new Error(`Failed to save service rates: ${rateError?.message || 'Unknown error'}`);
        }
      }
      
      setSaveSuccess(true);
      
      // Navigate back to rate table after successful save
      setTimeout(() => {
        navigate('/rate-table');
      }, 1500);
    } catch (error) {
      console.error('Error saving provider:', error);
      setSaveError(`Error saving provider: ${error.message}`);
      setSaveSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/rate-table');
  };

  if (loadingServiceTypes) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={handleCancel}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4">
            {isEditMode ? 'Edit Provider' : 'Add New Provider'}
          </Typography>
        </Box>
        
        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Provider {isEditMode ? 'updated' : 'added'} successfully!
          </Alert>
        )}
        
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            {/* Basic Information Section */}
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Provider Name"
                  value={providerName}
                  onChange={(e) => setProviderName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    labelId="status-label"
                    value={status}
                    label="Status"
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="net-terms-label">Net Terms</InputLabel>
                  <Select
                    labelId="net-terms-label"
                    value={netTerms}
                    label="Net Terms"
                    onChange={(e) => setNetTerms(e.target.value)}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    <MenuItem value={10}>Net 10</MenuItem>
                    <MenuItem value={15}>Net 15</MenuItem>
                    <MenuItem value={30}>Net 30</MenuItem>
                    <MenuItem value={45}>Net 45</MenuItem>
                    <MenuItem value={60}>Net 60</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Default Rates Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 2 }}>
                  Default Rates (for automatic enrollment in new service types)
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Default Hourly Rate"
                  type="number"
                  value={defaultHourlyRate}
                  onChange={(e) => setDefaultHourlyRate(e.target.value)}
                  helperText="Used for new hourly service types"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Default Flat Rate"
                  type="number"
                  value={defaultFlatRate}
                  onChange={(e) => setDefaultFlatRate(e.target.value)}
                  helperText="Used for new flat service types"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Default Refusal Rate"
                  type="number"
                  value={defaultRefusalRate}
                  onChange={(e) => setDefaultRefusalRate(e.target.value)}
                  helperText="Used for refusals on flat service types"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Email Configuration Section */}
            <Typography variant="h6" gutterBottom>
              Email Configuration
            </Typography>
            <Box sx={{ mb: 3 }}>
              <MultipleEmailManager 
                providerId={isEditMode ? id : null}
                legacyEmail={null}
                onEmailsChange={(emails) => {
                  console.log('Provider emails updated:', emails);
                }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Service Type Rate Configuration Section */}
            <Typography variant="h6" gutterBottom>
              Service Type Rate Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configure how this contractor should be paid for each type of service.
              You can choose between flat rate (per location) or hourly rate for each service type.
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              {serviceTypes.length === 0 ? (
                <Alert severity="info">
                  No service types configured. Please add service types first.
                </Alert>
              ) : (
                serviceTypes.map(serviceType => (
                  <ServiceRateConfiguration
                    key={serviceType.id}
                    serviceType={serviceType}
                    currentConfig={serviceRateConfigs[serviceType.id]}
                    onChange={handleServiceRateChange}
                    defaultHourlyRate={defaultHourlyRate}
                    defaultFlatRate={defaultFlatRate}
                    defaultRefusalRate={defaultRefusalRate}
                  />
                ))
              )}
            </Box>

            {/* Action Buttons */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Button 
                variant="outlined" 
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                disabled={loading || serviceTypes.length === 0}
              >
                {isEditMode ? 'Update Provider' : 'Add Provider'}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default AddEditProvider;
